import { ApplicationConfig, provideZoneChangeDetection, inject } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors, HttpInterceptorFn } from '@angular/common/http';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';

import { routes } from './app.routes';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, throwError } from 'rxjs';

// Functional interceptor for Angular 17+
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'current_user';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);

  // console.log('🔧 Interceptor called for:', req.url);

  // Skip token attachment for public endpoints
  if (isPublicEndpoint(req.url)) {
    // console.log('🔓 Public endpoint, skipping token:', req.url);
    return next(req);
  } else {
    // console.log('🔒 Protected endpoint, will attach token:', req.url);
  }

  // Add auth token to request if available
  const token = getTokenFromStorage();
  // console.log('🔑 Token from service:', token);

  if (token && !isTokenExpired(token)) {
    // console.log('✅ Adding token to request:', req.url);
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  } else if (token && isTokenExpired(token)) {
    // console.log('⏰ Token expired, not attaching to request:', req.url);
  } else {
    // console.log('❌ No token available for request:', req.url);
  }

  return next(req).pipe(
    catchError((error) => {
      // console.log('🚨 HTTP Error:', error.status, 'for URL:', req.url);
      if (error.status === 401 && !req.url.includes('/auth/refresh')) {
        snackBar.open('Session expired, please login again.', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      clearAuthStorage();
        router.navigate(['/login']);
      } else if (error.status === 403) {
        snackBar.open('Access denied. You do not have permission to perform this action.', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
      return throwError(() => error);
    })
  );
};

// Helper functions
function isPublicEndpoint(url: string): boolean {
  // Extract the path from the full URL
  const urlObj = new URL(url);
  const path = urlObj.pathname;

  const publicEndpoints = [
    '/auth/login',
    '/auth/register',
    '/auth/refresh',
    '/health',
    '/'
  ];

  const isPublic = publicEndpoints.includes(path);
  // console.log(`🔍 URL: ${url} -> Path: ${path} -> Is Public: ${isPublic}`);

  // Check for exact matches
  return isPublic;
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= expiry;
  } catch {
    return true;
  }
}

function getTokenFromStorage(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function clearAuthStorage(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor])
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideClientHydration(withEventReplay()),
    provideAnimations(),
  ]
};
